import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Archive,
    BadgePercent,
    BookOpen,
    Brain,
    Download,
    Loader2,
    PanelLeftClose,
    PanelLeftOpen,
    Save,
    ScanLine,
} from "lucide-react";
import { toast } from "sonner";
import { kusurSorgu, pollAsyncJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import KusurFormSidebar from "@/components/kusur/KusurFormSidebar";
import KusurKayitlarTab from "@/components/kusur/KusurKayitlarTab";
import KusurRehberPanel from "@/components/kusur/KusurRehberPanel";
import KusurSonucDashboard from "@/components/kusur/KusurSonucDashboard";
import KusurWorkflowStrip from "@/components/kusur/KusurWorkflowStrip";
import { EMPTY_FORM, TABS } from "@/components/kusur/kusurConstants";
import {
    applyFormFields,
    applyFormFromApi,
    clearDraftStorage,
    deriveWorkflowStep,
    loadDraft,
    resetFormState,
    saveDraft,
} from "@/components/kusur/kusurFormUtils";

const TAB_ICONS = {
    analiz: BadgePercent,
    kayitlar: Archive,
    rehber: BookOpen,
};

export default function KusurSorgulamaPage() {
    const [searchParams] = useSearchParams();
    const initialDosyaId = searchParams.get("dosya_id") || "";
    const draft = loadDraft();

    const [tab, setTab] = useState("analiz");
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [sorguModu, setSorguModu] = useState(draft?.sorguModu || (initialDosyaId ? "sistem" : "anlik"));
    const [loading, setLoading] = useState(false);
    const [kayitlarLoading, setKayitlarLoading] = useState(false);
    const [hizliOkuma, setHizliOkuma] = useState(false);
    const [sabitler, setSabitler] = useState(null);
    const [sonuc, setSonuc] = useState(null);
    const [okumaLog, setOkumaLog] = useState(null);
    const [tutanakOnizleme, setTutanakOnizleme] = useState(null);
    const [kayitId, setKayitId] = useState(null);
    const [kayitlar, setKayitlar] = useState([]);
    const [dosyalar, setDosyalar] = useState([]);
    const [dosyaArama, setDosyaArama] = useState("");
    const [dosyaListeLoading, setDosyaListeLoading] = useState(false);
    const [dosyaDoldurma, setDosyaDoldurma] = useState(false);
    const [jobProgress, setJobProgress] = useState("");
    const [evraklar, setEvraklar] = useState([]);
    const [evrakGruplu, setEvrakGruplu] = useState({});
    const [selectedEvrakIds, setSelectedEvrakIds] = useState(draft?.selectedEvrakIds || []);
    const [evrakLoading, setEvrakLoading] = useState(false);
    const [uploads, setUploads] = useState({ tutanak: [], eksper: [], foto: [], video: [], diger: [] });
    const [internetArastir, setInternetArastir] = useState(draft?.internetArastir ?? true);
    const [dosyayaUygula, setDosyayaUygula] = useState(draft?.dosyayaUygula ?? false);
    const [form, setForm] = useState({
        ...EMPTY_FORM,
        ...(draft?.form || {}),
        sigorta_dosyasi_id: initialDosyaId || draft?.form?.sigorta_dosyasi_id || "",
    });

    const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    useEffect(() => {
        saveDraft({ sorguModu, form, selectedEvrakIds, internetArastir, dosyayaUygula });
    }, [sorguModu, form, selectedEvrakIds, internetArastir, dosyayaUygula]);

    const loadSabitler = useCallback(async () => {
        try {
            setSabitler(await kusurSorgu.sabitler());
        } catch {
            toast.error("Sabitler yüklenemedi");
        }
    }, []);

    const loadKayitlar = useCallback(async () => {
        setKayitlarLoading(true);
        try {
            const params =
                form.sigorta_dosyasi_id && sorguModu === "sistem"
                    ? { dosya_id: form.sigorta_dosyasi_id }
                    : {};
            const r = await kusurSorgu.liste(params);
            setKayitlar(r.items || []);
        } catch {
            setKayitlar([]);
        } finally {
            setKayitlarLoading(false);
        }
    }, [form.sigorta_dosyasi_id, sorguModu]);

    const loadEvraklar = useCallback(
        async (dosyaId) => {
            if (!dosyaId || sorguModu !== "sistem") {
                setEvraklar([]);
                setEvrakGruplu({});
                return;
            }
            setEvrakLoading(true);
            try {
                const r = await kusurSorgu.dosyaEvraklar(dosyaId);
                setEvraklar(r.items || []);
                setEvrakGruplu(r.gruplu || {});
                if (r.onerilen_evrak_ids?.length && !selectedEvrakIds.length) {
                    setSelectedEvrakIds(r.onerilen_evrak_ids);
                }
            } catch {
                setEvraklar([]);
            } finally {
                setEvrakLoading(false);
            }
        },
        [sorguModu, selectedEvrakIds.length],
    );

    const loadDosyalar = useCallback(async (q = "") => {
        setDosyaListeLoading(true);
        try {
            const r = await kusurSorgu.dosyaListe({ limit: 300, q: q || undefined });
            setDosyalar(r.items || []);
        } catch {
            setDosyalar([]);
            toast.error("Dosya listesi yüklenemedi");
        } finally {
            setDosyaListeLoading(false);
        }
    }, []);

    const filteredDosyalar = useMemo(() => {
        const q = dosyaArama.trim().toLowerCase();
        if (!q) return dosyalar;
        return dosyalar.filter(
            (d) =>
                (d.label || "").toLowerCase().includes(q) ||
                (d.dosya_no || "").toLowerCase().includes(q) ||
                (d.plaka || "").toLowerCase().includes(q) ||
                (d.muvekkil || "").toLowerCase().includes(q),
        );
    }, [dosyalar, dosyaArama]);

    const toplamDelil = useMemo(() => {
        const up = Object.values(uploads).reduce((n, arr) => n + (arr?.length || 0), 0);
        const ev = sorguModu === "sistem" ? selectedEvrakIds.length : 0;
        return up + ev;
    }, [uploads, selectedEvrakIds, sorguModu]);

    const workflowStep = deriveWorkflowStep({ toplamDelil, okumaLog, sonuc, kayitId });

    useEffect(() => {
        loadSabitler();
        loadDosyalar();
    }, [loadSabitler, loadDosyalar]);

    useEffect(() => {
        if (initialDosyaId) onDosyaSec(initialDosyaId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDosyaId]);

    useEffect(() => {
        if (tab === "kayitlar") loadKayitlar();
    }, [tab, loadKayitlar]);

    useEffect(() => {
        if (form.sigorta_dosyasi_id && sorguModu === "sistem") {
            loadEvraklar(form.sigorta_dosyasi_id);
        }
    }, [form.sigorta_dosyasi_id, sorguModu, loadEvraklar]);

    const unwrapJobPayload = (r) => {
        if (!r || typeof r !== "object") return r;
        if (r.sonuc != null || r.form != null || r.okuma != null) return r;
        if (r.result && typeof r.result === "object") return r.result;
        return r;
    };

    const resolveAsync = async (initial) => {
        if (initial?.async && initial?.job_id) {
            return pollAsyncJob(initial.job_id, {
                onProgress: (d) => setJobProgress(d.message || (d.progress ? `${d.progress}%` : "")),
            }).then(unwrapJobPayload);
        }
        return unwrapJobPayload(initial);
    };

    const onDosyaSec = async (id) => {
        if (!id) return;
        setSorguModu("sistem");
        setDosyaDoldurma(true);
        setJobProgress("Kuyruğa alınıyor…");
        setSonuc(null);
        setTutanakOnizleme(null);
        try {
            const initial = await kusurSorgu.dosyaOtomatikDoldur(id, { use_ai: true, deep_tutanak: true });
            const r = await resolveAsync(initial);
            setForm((p) => applyFormFromApi({ ...p, sigorta_dosyasi_id: String(id) }, r));
            setSelectedEvrakIds(r.selected_evrak_ids || []);
            if (r.tutanak_struct) setTutanakOnizleme(r.tutanak_struct);
            if (r.evraklar) {
                setEvraklar(r.evraklar.items || []);
                setEvrakGruplu(r.evraklar.gruplu || {});
            }
            if (r.okuma) setOkumaLog(r.okuma);
            toast.success(`${r.form?.dosya_no || "Dosya"} hazır · ${r.okuma?.okunan_sayisi || 0} belge`);
        } catch (e) {
            toast.error(e?.response?.data?.error || e?.message || "Otomatik doldurma başarısız");
            try {
                const pre = await kusurSorgu.dosyaOnDoldur(id);
                setForm((p) => applyFormFields({ ...p, sigorta_dosyasi_id: String(id) }, pre));
                setSelectedEvrakIds(pre.otomatik_evrak_ids || pre.onerilen_evrak_ids || []);
            } catch {
                /* ignore */
            }
        } finally {
            setDosyaDoldurma(false);
            setJobProgress("");
        }
    };

    const toggleEvrak = (id) => {
        setSelectedEvrakIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    };

    const selectOnerilen = () => {
        const ids = evraklar.filter((e) => e.kusur_analiz_uygun).map((e) => e.id);
        if (ids.length) setSelectedEvrakIds(ids);
        else {
            const fallback = [];
            ["tutanak", "eksper", "foto"].forEach((k) => {
                const first = evrakGruplu[k]?.[0];
                if (first) fallback.push(first.id);
            });
            setSelectedEvrakIds(fallback);
        }
    };

    const addFiles = (key, fileList) => {
        if (!fileList?.length) return;
        setUploads((p) => ({ ...p, [key]: [...(p[key] || []), ...Array.from(fileList)] }));
    };
    const removeFile = (key, index) =>
        setUploads((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== index) }));

    const buildPayload = () => ({
        ...form,
        sigorta_dosyasi_id: sorguModu === "sistem" && form.sigorta_dosyasi_id ? form.sigorta_dosyasi_id : null,
        evrak_ids: sorguModu === "sistem" ? selectedEvrakIds : [],
        mevcut_kusur_orani: form.mevcut_kusur_orani !== "" ? Number(form.mevcut_kusur_orani) : null,
        baslik: form.baslik || (sorguModu === "anlik" ? "Anlık kusur sorgusu" : ""),
    });

    const appendFormData = (fd) => {
        const payload = buildPayload();
        fd.append("payload", JSON.stringify(payload));
        fd.append("evrak_ids", JSON.stringify(payload.evrak_ids || []));
        fd.append("internet_arastir", internetArastir ? "1" : "0");
        Object.entries(uploads).forEach(([key, files]) => files.forEach((f) => fd.append(key, f)));
        return fd;
    };

    const hizliOkuVeDoldur = async () => {
        if (toplamDelil === 0) {
            toast.error("En az bir evrak veya dosya seçin");
            return;
        }
        setHizliOkuma(true);
        setJobProgress("Okuma başlatılıyor…");
        try {
            const fd = appendFormData(new FormData());
            fd.append("use_ai", "1");
            const r = await resolveAsync(await kusurSorgu.hizliOku(fd));
            setOkumaLog(r.okuma);
            if (r.tutanak_derin) setTutanakOnizleme(r.tutanak_derin);
            setForm((p) => applyFormFields(p, { form: r.form_oneri || {} }));
            toast.success(r.form_dolduruldu ? "Form güncellendi" : "Belgeler okundu");
        } catch (e) {
            toast.error(e?.response?.data?.error || e?.message || "Hızlı okuma başarısız");
        } finally {
            setHizliOkuma(false);
            setJobProgress("");
        }
    };

    const analizYap = async (kaydet = false) => {
        if (toplamDelil === 0 && !form.olay_ozeti?.trim() && !form.beyan_metni?.trim()) {
            toast.error("Delil veya olay metni gerekli");
            return;
        }
        setLoading(true);
        setJobProgress("AI analizi…");
        try {
            const fd = appendFormData(new FormData());
            fd.append("kaydet", kaydet ? "1" : "0");
            fd.append("dosyaya_uygula", dosyayaUygula && sorguModu === "sistem" ? "1" : "0");
            fd.append("deep_tutanak", "1");
            const r = await resolveAsync(await kusurSorgu.analiz(fd));
            const analizSonuc = r.sonuc || (r.taraf_a ? r : null);
            if (!analizSonuc?.taraf_a) throw new Error(r.error || "Analiz sonucu boş");
            setSonuc(analizSonuc);
            if (r.item?.id) {
                setKayitId(r.item.id);
                toast.success("Analiz kaydedildi");
            } else {
                toast.success(`Tamam — Müvekkil %${analizSonuc.taraf_a?.kusur_orani ?? "?"}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Analiz hatası");
        } finally {
            setLoading(false);
            setJobProgress("");
        }
    };

    const kaydet = async () => {
        if (!sonuc) return toast.error("Önce analiz yapın");
        setLoading(true);
        try {
            const r = await kusurSorgu.kaydet({
                girdi: buildPayload(),
                sonuc,
                dosyaya_uygula: dosyayaUygula && sorguModu === "sistem",
            });
            setKayitId(r.item?.id);
            toast.success("Kayıt oluşturuldu");
            loadKayitlar();
        } catch (e) {
            toast.error(e?.response?.data?.error || "Kayıt hatası");
        } finally {
            setLoading(false);
        }
    };

    const pdfIndir = async (id) => {
        setLoading(true);
        try {
            const blob = await kusurSorgu.pdf({ id: id || kayitId, sonuc: id ? undefined : sonuc });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `kusur-${id || kayitId || "taslak"}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("PDF oluşturulamadı");
        } finally {
            setLoading(false);
        }
    };

    const temizleTaslak = () => {
        if (!window.confirm("Taslak sıfırlansın mı?")) return;
        clearDraftStorage();
        const reset = resetFormState();
        setForm(reset.form);
        setUploads(reset.uploads);
        setSelectedEvrakIds(reset.selectedEvrakIds);
        setSonuc(null);
        setOkumaLog(null);
        setTutanakOnizleme(null);
        setKayitId(null);
        toast.success("Taslak temizlendi");
    };

    const loadKayitIntoAnaliz = async (k) => {
        setTab("analiz");
        if (k.girdi) setForm((p) => ({ ...p, ...k.girdi }));
        if (k.sonuc) setSonuc(k.sonuc);
        setKayitId(k.id);
        if (k.sigorta_dosyasi_id) {
            setSorguModu("sistem");
            setField("sigorta_dosyasi_id", String(k.sigorta_dosyasi_id));
        }
        toast.info("Kayıt analiz ekranına yüklendi");
    };

    const silKayit = async (id) => {
        if (!window.confirm("Kayıt silinsin mi?")) return;
        await kusurSorgu.sil(id);
        loadKayitlar();
        toast.success("Silindi");
    };

    const busy = loading || hizliOkuma || dosyaDoldurma;

    return (
        <div className="flex flex-col h-[calc(100vh-3.5rem)] min-h-[520px] bg-[#070B14] text-slate-200">
            {/* Header */}
            <header className="shrink-0 border-b border-white/10 bg-[#0a0f18]/95 backdrop-blur px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 min-w-0 mr-2">
                        <BadgePercent className="h-5 w-5 text-[#a31d21] shrink-0" />
                        <div>
                            <h1 className="text-sm font-semibold text-white leading-tight">Kusur Sorgulama Merkezi</h1>
                            <p className="text-[10px] text-slate-500 hidden sm:block">
                                KTT · AI analiz · itiraz stratejisi · PDF
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1 flex-1">
                        {TABS.map((t) => {
                            const Icon = TAB_ICONS[t.id];
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setTab(t.id)}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] border transition ${
                                        tab === t.id
                                            ? "bg-[#a31d21]/20 border-[#a31d21]/40 text-white"
                                            : "border-white/10 text-slate-400 hover:text-slate-200"
                                    }`}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                    {tab === "analiz" ? (
                        <div className="flex flex-wrap items-center gap-1">
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-[10px] border-sky-500/30 text-sky-200 px-2"
                                disabled={busy}
                                onClick={hizliOkuVeDoldur}
                            >
                                {hizliOkuma ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ScanLine className="h-3 w-3 mr-1" />}
                                Hızlı Oku
                            </Button>
                            <Button size="sm" className="h-7 text-[10px] bg-[#a31d21] px-2" disabled={busy} onClick={() => analizYap(false)}>
                                {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Brain className="h-3 w-3 mr-1" />}
                                AI Analiz
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-[10px] border-white/10 px-2" disabled={busy} onClick={() => analizYap(true)}>
                                <Save className="h-3 w-3 mr-1" /> Kaydet
                            </Button>
                            {sonuc ? (
                                <>
                                    <Button size="sm" variant="outline" className="h-7 text-[10px] border-white/10 px-2" disabled={loading} onClick={kaydet}>
                                        <Save className="h-3 w-3" />
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-7 text-[10px] border-white/10 px-2" disabled={loading} onClick={() => pdfIndir()}>
                                        <Download className="h-3 w-3" />
                                    </Button>
                                </>
                            ) : null}
                            <button
                                type="button"
                                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-white/10 text-slate-400 hover:text-white"
                                onClick={() => setSidebarOpen((v) => !v)}
                                title={sidebarOpen ? "Paneli gizle" : "Paneli göster"}
                            >
                                {sidebarOpen ? <PanelLeftClose className="h-3.5 w-3.5" /> : <PanelLeftOpen className="h-3.5 w-3.5" />}
                            </button>
                        </div>
                    ) : null}
                </div>
                {jobProgress ? (
                    <div className="flex items-center gap-1.5 text-[10px] text-sky-300 mt-1.5">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {jobProgress}
                    </div>
                ) : null}
            </header>

            {tab === "analiz" ? <KusurWorkflowStrip activeStep={workflowStep} /> : null}

            <div className="flex-1 min-h-0 overflow-hidden">
                {tab === "analiz" ? (
                    <div className="flex h-full">
                        {sidebarOpen ? (
                            <aside className="w-[min(100%,340px)] shrink-0 border-r border-white/10 p-2 overflow-hidden">
                                <KusurFormSidebar
                                    sorguModu={sorguModu}
                                    setSorguModu={setSorguModu}
                                    form={form}
                                    setField={setField}
                                    dosyalar={dosyalar}
                                    filteredDosyalar={filteredDosyalar}
                                    dosyaArama={dosyaArama}
                                    setDosyaArama={setDosyaArama}
                                    dosyaListeLoading={dosyaListeLoading}
                                    dosyaDoldurma={dosyaDoldurma}
                                    jobProgress={jobProgress}
                                    onDosyaSec={onDosyaSec}
                                    loadDosyalar={loadDosyalar}
                                    evraklar={evraklar}
                                    evrakGruplu={evrakGruplu}
                                    selectedEvrakIds={selectedEvrakIds}
                                    toggleEvrak={toggleEvrak}
                                    selectOnerilen={selectOnerilen}
                                    evrakLoading={evrakLoading}
                                    uploads={uploads}
                                    addFiles={addFiles}
                                    removeFile={removeFile}
                                    toplamDelil={toplamDelil}
                                    internetArastir={internetArastir}
                                    setInternetArastir={setInternetArastir}
                                    dosyayaUygula={dosyayaUygula}
                                    setDosyayaUygula={setDosyayaUygula}
                                    temizleTaslak={temizleTaslak}
                                />
                            </aside>
                        ) : null}
                        <main className="flex-1 min-w-0 p-2 overflow-hidden">
                            <KusurSonucDashboard
                                sonuc={sonuc}
                                sabitler={sabitler}
                                okumaLog={okumaLog}
                                tutanakOnizleme={tutanakOnizleme}
                                form={form}
                            />
                        </main>
                    </div>
                ) : null}

                {tab === "kayitlar" ? (
                    <div className="h-full overflow-auto p-3">
                        <KusurKayitlarTab
                            kayitlar={kayitlar}
                            loading={kayitlarLoading}
                            onRefresh={loadKayitlar}
                            onPdf={pdfIndir}
                            onLoadKayit={loadKayitIntoAnaliz}
                            onSil={silKayit}
                        />
                    </div>
                ) : null}

                {tab === "rehber" ? (
                    <div className="h-full overflow-auto p-3">
                        <KusurRehberPanel sabitler={sabitler} />
                    </div>
                ) : null}
            </div>
        </div>
    );
}
